import { useMemo } from 'react'
import StringFormat from 'utils/StringFormat'
import * as yup from 'yup'
import { ValidationError } from 'yup'

export const useValidationSchema = () => {
    return useMemo(() => {
        return yup.lazy(({ cardQuantity = 0 }, { context }) => {
            const {
                minPerCard,
                maxPerCard,
                minPerValue,
                maxPerValue,
                product
            } = context

            const minValue = StringFormat.convertToMonetary(
                minPerValue.toString() + '00' // 1 => R$ 0,01 / 1.0 => R$ 0,01 / 100 => R$ 1,00
            )

            const maxValue = StringFormat.convertToMonetary(
                maxPerValue.toString() + '00' // 1 => R$ 0,01 / 1.0 => R$ 0,01 / 100 => R$ 1,00
            )

            const errorMessages = {
                minValue: `O valor deve ser <br> maior ou igual que ${minValue}`,
                maxValue: `O valor deve ser <br> menor ou igual que <br>${maxValue}`,
                minCard: `A quantidade de cartões  <br> deve ser maior ou igual a ${minPerCard}`,
                maxCard: `A quantidade de cartões  <br> deve ser menor ou igual a ${maxPerCard}`,
                typeValue: 'Não permitimos que o <br> valor seja nulo',
                typeCard:
                    'Não permitimos que a <br> quantidade de cartões <br> seja nula',
                sumQuantity: `A soma da quantidade de <br > colaboradores de todos os benefícios <br> deve ser igual ou maior que ${cardQuantity} <br> (Qtde de cartões ${product.name})`
            }

            return yup
                .object({
                    cardQuantity: yup
                        .number()
                        .typeError(errorMessages.typeCard)
                        .min(minPerCard, errorMessages.minCard)
                        .max(maxPerCard, errorMessages.maxCard),
                    monthlyValue: yup
                        .number()
                        .typeError(errorMessages.typeValue)
                        .min(minPerValue, errorMessages.minValue)
                        .max(maxPerValue, errorMessages.maxValue),
                    isPat: yup.boolean(),
                    groups: yup.lazy((groups) => {
                        const schema = Object.keys(groups).reduce(
                            (schema, key) => {
                                schema[key] = yup
                                    .object({
                                        cardQuantity: yup
                                            .number()
                                            .nullable(true)
                                            .transform((_, val) =>
                                                val === Number(val) ? val : null
                                            )
                                            .test(
                                                'min-check',
                                                errorMessages.minCard,
                                                function (value) {
                                                    if (
                                                        value === null ||
                                                        value === 0
                                                    ) {
                                                        return true
                                                    }
                                                    return value >= minPerCard
                                                }
                                            )
                                            .max(
                                                maxPerCard,
                                                errorMessages.maxCard
                                            ),
                                        monthlyValue: yup
                                            .number()
                                            .nullable(true)
                                            .transform((_, val) =>
                                                val === Number(val) ? val : null
                                            )
                                            .test(
                                                'min-check',
                                                errorMessages.minValue,
                                                function (value) {
                                                    if (
                                                        value === null ||
                                                        value === 0
                                                    ) {
                                                        return true
                                                    }
                                                    return value >= minPerValue
                                                }
                                            )
                                            .max(
                                                maxPerValue,
                                                errorMessages.maxValue
                                            )
                                    })
                                    .test(
                                        'groupQuantityAndMonthly',
                                        function (
                                            value,
                                            { path, createError }
                                        ) {
                                            const {
                                                cardQuantity,
                                                monthlyValue
                                            } = value

                                            if (
                                                cardQuantity !== null &&
                                                cardQuantity !== 0 &&
                                                (!monthlyValue ||
                                                    monthlyValue >
                                                        maxPerValue ||
                                                    monthlyValue < minPerValue)
                                            ) {
                                                return createError({
                                                    path: `${path}.monthlyValue`,
                                                    message:
                                                        errorMessages.minValue
                                                })
                                            }

                                            if (
                                                monthlyValue !== null &&
                                                monthlyValue !== 0 &&
                                                (!cardQuantity ||
                                                    cardQuantity > maxPerCard ||
                                                    cardQuantity < minPerCard)
                                            ) {
                                                return createError({
                                                    path: `${path}.cardQuantity`,
                                                    message:
                                                        errorMessages.minCard
                                                })
                                            }

                                            return true
                                        }
                                    )
                                return schema
                            },
                            {}
                        )

                        return yup.object(schema)
                    })
                })
                .test(
                    'sumCardQuantity',
                    errorMessages.sumQuantity,
                    function (value, { createError }) {
                        const { cardQuantity, groups } = value

                        if (!cardQuantity) {
                            return true
                        }

                        const sumGroupCardQuantity = Object.keys(groups).reduce(
                            (sum, key) => sum + (groups[key].cardQuantity || 0),
                            0
                        )
                        const sumIsGreaterOrEqual =
                            sumGroupCardQuantity >= cardQuantity

                        if (sumIsGreaterOrEqual) {
                            return true
                        }

                        let errors = Object.keys(groups).map((key) => {
                            return new ValidationError(
                                errorMessages.sumQuantity,
                                sumIsGreaterOrEqual,
                                `groups.${key}.cardQuantity`
                            )
                        })
                        errors = [
                            ...errors,
                            new ValidationError(
                                `* A soma da quantidade de colaboradores de todos os benefícios deve ser igual ou maior que ${cardQuantity} (Qtde de cartões ${product.name}).`,
                                sumIsGreaterOrEqual,
                                'sumCardQuantity'
                            )
                        ]

                        return createError({
                            message: () => errors
                        })
                    }
                )
        })
    }, [])
}
